import React, { useState, useMemo } from "react";
import Highstock from "highcharts/highstock";
import ReactHighcharts from "react-highcharts";
import Skeleton from "react-loading-skeleton";
import { RoundedSelect } from "../tailwind/atoms";
import { TrendsIcon } from "../../assets/images";
import moment from "moment";
import { Select } from "antd";

const Option  = Select.Option;
const Charts = ReactHighcharts.withHighcharts(Highstock);
const COLORS = [
  "#2168F3", "#00B37E", "#F59E0B", "#9333EA", "#F43F5E",
  "#10B981", "#EF4444", "#6366F1", "#E11D48", "#0EA5E9"
];

const BenefitsTrends = ({
  data = {},
  loading = false,
  trendKeyMap = {
    "Week-on-Week": "week_on_week",
    "Month-on-Month": "month_on_month",
  },
  selectedTrend = "Week-on-Week",
  servicesMap = {},
  currentService = '',
  handleServiceChange = () => {}
}) => {
  const [metricType, setMetricType] = useState("amount");
  const [selectedOption, setSelectedOption] = useState(selectedTrend);
  const [selectedBenefits, setSelectedBenefits] = useState([]);

  const trendData = data?.[trendKeyMap[selectedOption]] || {};
  const allBenefits = Object.keys(trendData || {});

  React.useEffect(() => {
    if (allBenefits.length && selectedBenefits.length === 0) {
      setSelectedBenefits(allBenefits.slice(0, 8));
    }
  }, [allBenefits]);

  const chartConfig = useMemo(() => {
    if (!trendData || Object.keys(trendData).length === 0) return null;

    const benefitKeys = selectedBenefits.length
      ? selectedBenefits
      : allBenefits;

    const categoriesSet = new Set();
    benefitKeys.forEach((b) => {
      (trendData[b]?.labels || []).forEach((l) => categoriesSet.add(l));
    });
    const categories = Array.from(categoriesSet).sort((a, b) => {
      const currentYear = new Date().getFullYear();
      return (
        moment(`${a}-${currentYear}`, "DD-MMM-YYYY").toDate() -
        moment(`${b}-${currentYear}`, "DD-MMM-YYYY").toDate()
      );
    });

    const series = benefitKeys.map((benefit, idx) => {
      const dataset = trendData[benefit]?.[metricType] || {};
      const points = categories.map((c) => dataset[c] ?? null);
      return {
        name: benefit,
        data: points,
        color: COLORS[allBenefits.indexOf(benefit) % COLORS.length],
        lineWidth: benefitKeys.length > 6 ? 1.5 : 2.5,
        marker: {
          enabled: benefitKeys.length <= 10,
          radius: benefitKeys.length > 10 ? 2 : 4,
        },
      };
    });

    return {
      chart: { type: "line", height: 450, zoomType: "x" },
      title: { text: "" },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: { fontSize: "11px", color: "#555" },
        navigation: { activeColor: "#2168F3", animation: true },
        maxHeight: 80,
      },
      xAxis: {
        categories,
        title: { text: selectedOption },
        labels: {
          rotation: categories.length > 6 ? -45 : 0,
          style: { fontSize: "10px" },
        },
      },
      yAxis: {
        title: {
          text:
            metricType === "volume" ? "Number of Claims" : "Amount (₹)",
        },
        allowDecimals: false,
        gridLineDashStyle: "Dash",
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: "#fff",
        borderRadius: 8,
        borderColor: "#ddd",
        shadow: true,
        formatter: function () {
          return `
            <div style="padding:4px 8px">
              <strong>${this.x}</strong><br/>
              ${this.points
                .map(
                  (p) =>
                    `<span style="color:${p.color}">●</span> ${
                      p.series.name
                    }: <strong>${
                      metricType === "amount"
                        ? `₹${new Intl.NumberFormat("en-IN", {
                            maximumFractionDigits: 0,
                          }).format(p.y)}`
                        : p.y
                    }</strong>`
                )
                .join("<br/>")}
            </div>
          `;
        },
      },
      series,
    };
  }, [data, metricType, selectedOption, selectedBenefits]);

  return (
    <div className="bg-white p-6 shadow-sm rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-base text-gray-700 leading-none flex gap-2 items-center">
          <img src={TrendsIcon} className="h-4 w-4" alt="Trends Icon" />
          <span className="font-fig-semibold">Benefit-wise Trends</span>
        </h1>

        <div className="flex gap-4 items-center">
          {/* Metric toggle */}
          <div className="flex bg-gray-100 rounded-md p-1 space-x-1 overflow-x-auto">
            {["volume", "amount"].map((type) => (
              <button
                key={type}
                onClick={() => setMetricType(type)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200
                  ${
                    metricType === type
                      ? "bg-white text-gray-800 shadow"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                {type === "volume" ? "Count" : "Value (₹)"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-4 items-center">
          {/* Service dropdown */}
          <RoundedSelect
            listClasses="w-34"
            selectOptions={Object.values(servicesMap)}
            selectValues={Object.keys(servicesMap).map((_, i) => i)}
            curvalue={servicesMap[currentService] || 'Overall'}
            selectHandler={(index) =>
              handleServiceChange('benefit_trends', Object.keys(servicesMap)[index])
            }
          />

          {/* Trend dropdown */}
          <RoundedSelect
            listClasses="w-44"
            selectOptions={Object.keys(trendKeyMap)}
            selectValues={Object.keys(trendKeyMap).map((_, i) => i)}
            curvalue={selectedOption}
            selectHandler={(index) =>
              setSelectedOption(Object.keys(trendKeyMap)[index])
            }
          />
        </div>
      </div>
      <div className="mb-4 flex flex-col items-right w-11/12 ml-16 gap-4">
          {/* Benefit selector */}
          <Select
            mode="multiple"
            allowClear
            placeholder="Select Benefits"
            value={selectedBenefits}
            onChange={(vals) => setSelectedBenefits(vals)}
            maxTagCount="responsive"
            tagRender={({ label, value, closable, onClose }) => {
              const idx = allBenefits.indexOf(value);
              const color = COLORS[idx % COLORS.length];

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#fff",
                    border: `1.5px solid ${color}`,
                    borderRadius: "8px",
                    padding: "2px 6px",
                    margin: "2px",
                    fontSize: "13px",
                    color: "#333",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: color,
                      marginRight: "6px",
                    }}
                  />
                  {label}
                  {closable && (
                    <span
                      onClick={onClose}
                      style={{
                        marginLeft: "6px",
                        color: "#888",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ✕
                    </span>
                  )}
                </div>
              );
            }}
          >
            {allBenefits.map((b, idx) => {
              const color = COLORS[idx % COLORS.length];
              return (
                <Option key={b} value={b}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: color,
                      }}
                    />
                    {b}
                  </div>
                </Option>
              );
            })}
          </Select>
      </div>

      {/* Chart */}
      {loading ? (
        <Skeleton width="100%" height={400} />
      ) : chartConfig ? (
        <Charts config={chartConfig} />
      ) : (
        <div className="w-full h-96 flex flex-col items-center justify-center text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 mb-2 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 3v18h18M3 9h18M9 21V9m6 12V9"
            />
          </svg>
          No data available
        </div>
      )}
    </div>
  );
};

export default BenefitsTrends;
